import { useCallback, useEffect, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';

export interface UsePlatformAdminResult {
  /** `true` only when `/admin/check` answered `isAdmin: true`. */
  isPlatformAdmin: boolean;
  loading: boolean;
  /**
   * `true` when `/admin/check` could not be answered (a `503` while SpiceDB is
   * unreachable, any other failed request). `isPlatformAdmin` is `false`, but
   * that is not a "no": render a could-not-verify state with `retry`, never an
   * access-denied one.
   */
  unavailable: boolean;
  /** Drop the cached answer for the current client and ask again. */
  retry: () => void;
}

type PlatformAdminAnswer = 'admin' | 'notAdmin' | 'unavailable';

interface CacheEntry {
  clientRef: ApiClient;
  promise: Promise<PlatformAdminAnswer>;
}

let cacheEntry: CacheEntry | null = null;

/** Clear the session-level platform-admin cache. Exported for tests and logout flows. */
export function resetPlatformAdminCache(): void {
  cacheEntry = null;
}

function fetchPlatformAdminAnswer(client: ApiClient): Promise<PlatformAdminAnswer> {
  // A same-reference hit serves the cached answer.
  //
  // The ApiClient reference does NOT change on an impersonation switch —
  // `ApiClientsProvider` deliberately keeps its clients and resolves the bearer
  // per-request through `tokenProvider`. This check therefore does NOT provide
  // cross-identity invalidation; `resetPlatformAdminCache()`, wired into
  // `useSchemaTypeCacheReset`, is what does. EAINT-798.
  if (cacheEntry?.clientRef === client) {
    return cacheEntry.promise;
  }

  const promise = client
    .get<{ isAdmin: boolean }>('/admin/check')
    .then((resp): PlatformAdminAnswer => (resp?.isAdmin === true ? 'admin' : 'notAdmin'))
    .catch((): PlatformAdminAnswer => {
      // A failed check is never cached: the next caller (or `retry`) asks again.
      if (cacheEntry?.clientRef === client) {
        cacheEntry = null;
      }
      return 'unavailable';
    });

  cacheEntry = { clientRef: client, promise };
  return promise;
}

/**
 * Returns whether the authenticated user is a platform admin. The answer is
 * cached for the module's lifetime (page session) so repeated calls do not
 * refetch. Fails closed: when the check cannot be answered `isPlatformAdmin`
 * is `false` and `unavailable` is `true`, so a guard can tell "could not
 * verify" from "not an admin".
 *
 * This is a UI gate only — the backend still enforces admin authorization.
 */
export function usePlatformAdmin(): UsePlatformAdminResult {
  const apiClients = useApiClientsOrNull();
  const [answer, setAnswer] = useState<PlatformAdminAnswer | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!apiClients?.permissionClient) {
      return;
    }

    let cancelled = false;
    // A new check (another client, or a retry) never shows the previous answer while it runs.
    setAnswer(null);
    setLoading(true);

    // fetchPlatformAdminAnswer maps every failure to 'unavailable', so it never rejects.
    void fetchPlatformAdminAnswer(apiClients.permissionClient).then((next) => {
      if (cancelled) return;
      setAnswer(next);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [apiClients, attempt]);

  const retry = useCallback(() => {
    if (cacheEntry?.clientRef === apiClients?.permissionClient) {
      cacheEntry = null;
    }
    setAttempt((n) => n + 1);
  }, [apiClients]);

  return {
    isPlatformAdmin: answer === 'admin',
    loading,
    unavailable: answer === 'unavailable',
    retry,
  };
}
